import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, GitMerge, ArrowRight } from 'lucide-react';

/**
 * Switch Node Configuration Component
 * 
 * Provides an intuitive UI for building switch/case logic with:
 * - Multiple case branches
 * - Default fallback branch
 * - Visual branch management
 * - Color-coded outputs
 */
const SwitchNodeConfig = ({ config, onChange }) => {
  const [variable, setVariable] = useState(config?.variable || '');
  const [cases, setCases] = useState(config?.cases || []);
  const [defaultCase, setDefaultCase] = useState(config?.defaultCase || {});

  const operators = [
    { value: 'equals', label: 'Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'starts_with', label: 'Starts With' },
    { value: 'ends_with', label: 'Ends With' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'matches_regex', label: 'Matches Pattern' },
  ];

  const caseColors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-orange-500',
    'bg-cyan-500',
    'bg-indigo-500',
    'bg-teal-500',
  ];

  const addCase = () => {
    const newCase = {
      id: `case_${Date.now()}`,
      value: '',
      operator: 'equals',
      targetNode: null,
      color: caseColors[cases.length % caseColors.length]
    };
    const updated = [...cases, newCase];
    setCases(updated);
    updateConfig(variable, updated, defaultCase);
  };

  const removeCase = (id) => {
    const updated = cases.filter(c => c.id !== id);
    setCases(updated);
    updateConfig(variable, updated, defaultCase);
  };

  const updateCase = (id, field, value) => {
    const updated = cases.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    );
    setCases(updated);
    updateConfig(variable, updated, defaultCase);
  };

  const updateVariable = (value) => {
    setVariable(value);
    updateConfig(value, cases, defaultCase);
  };

  const updateConfig = (varValue, casesValue, defaultValue) => {
    onChange({
      ...config,
      variable: varValue,
      cases: casesValue,
      defaultCase: defaultValue
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitMerge className="w-5 h-5 text-purple-500" />
          <h3 className="text-lg font-semibold">Switch Configuration</h3>
        </div>
        <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          {cases.length} case{cases.length !== 1 ? 's' : ''} + default
        </Badge>
      </div>

      {/* Variable Selector */}
      <Card className="border-purple-500/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Switch On Variable</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            placeholder="{{trigger.status}} or {{step1.type}}"
            value={variable}
            onChange={(e) => updateVariable(e.target.value)}
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Enter the variable to evaluate. Use {`{{variable.path}}`} syntax.
          </p>
        </CardContent>
      </Card>

      {/* Cases */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Case Branches</Label>
          <Button onClick={addCase} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Add Case
          </Button>
        </div>

        {cases.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="pt-6 pb-6 text-center">
              <p className="text-sm text-muted-foreground mb-3">
                No cases yet. Add cases to route to different paths.
              </p>
              <Button onClick={addCase} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add First Case
              </Button>
            </CardContent>
          </Card>
        ) : (
          cases.map((caseItem, index) => (
            <Card key={caseItem.id} className="border-purple-500/20">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${caseItem.color}`} />
                    <CardTitle className="text-sm font-medium">
                      Case {index + 1}
                    </CardTitle>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCase(caseItem.id)}
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Operator */}
                <div className="space-y-2">
                  <Label className="text-xs">Comparison Type</Label>
                  <Select
                    value={caseItem.operator}
                    onValueChange={(value) => updateCase(caseItem.id, 'operator', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {operators.map(op => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Case Value */}
                <div className="space-y-2">
                  <Label className="text-xs">Value to Match</Label>
                  <Input
                    placeholder="e.g., 'success', 'pending', 'error'"
                    value={caseItem.value}
                    onChange={(e) => updateCase(caseItem.id, 'value', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>

                {/* Visual Flow Indicator */}
                <div className="flex items-center gap-2 pt-2 text-xs text-muted-foreground">
                  <span>If {variable || '(variable)'}</span>
                  <span className="text-purple-600 font-medium">{caseItem.operator}</span>
                  <span>"{caseItem.value || '(value)'}"</span>
                  <ArrowRight className="w-3 h-3" />
                  <Badge variant="outline" className={`${caseItem.color} text-white border-0`}>
                    Branch {index + 1}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Default Case */}
      <Card className="border-gray-500/20 bg-gray-500/5">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-500" />
            <CardTitle className="text-sm font-medium">Default Case</CardTitle>
            <Badge variant="outline" className="text-xs">Fallback</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            The default case will be executed if none of the above cases match.
            This ensures your workflow always has a path forward.
          </p>
        </CardContent>
      </Card>

      {/* Switch Summary */}
      {cases.length > 0 && (
        <Card className="bg-purple-500/5 border-purple-500/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Switch Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Evaluating: <span className="font-mono text-foreground">{variable || '(not set)'}</span>
            </p>
            <div className="space-y-1">
              {cases.map((c, i) => (
                <div key={c.id} className="flex items-center gap-2 text-xs">
                  <div className={`w-2 h-2 rounded-full ${c.color}`} />
                  <span className="text-muted-foreground">
                    Case {i + 1}: {c.operator} "{c.value || '(empty)'}"
                  </span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-xs">
                <div className="w-2 h-2 rounded-full bg-gray-500" />
                <span className="text-muted-foreground">Default: All other values</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SwitchNodeConfig;
